import type { WidgetFilterField, WidgetFilterGroup, WidgetFilterRule } from '../types';

// Fields whose value is a Snowflake ID (backend expects a bare JSON number,
// see FilterEngine — `status/assignee/group/board/tag` must be `long`, never
// a string). Snowflake IDs exceed Number.MAX_SAFE_INTEGER, so they must never
// pass through JSON.parse/JSON.stringify as a JS number — we keep them as
// digit strings end-to-end and splice them into the JSON text unquoted.
const ID_FIELDS = new Set<WidgetFilterField>(['status', 'assignee', 'group', 'board', 'tag']);

function isGroup(node: WidgetFilterRule | WidgetFilterGroup): node is WidgetFilterGroup {
  return 'rules' in node;
}

function serializeIdScalar(value: unknown): string {
  const raw = String(value);
  // Only ever splice digits unquoted — anything else falls back to a quoted
  // string so a malformed value can't corrupt the JSON.
  return /^-?\d+$/.test(raw) ? raw : JSON.stringify(raw);
}

function serializeValue(field: WidgetFilterField, value: WidgetFilterRule['value']): string {
  const isId = ID_FIELDS.has(field);
  if (Array.isArray(value)) {
    return `[${value.map((v) => (isId ? serializeIdScalar(v) : JSON.stringify(v))).join(',')}]`;
  }
  return isId ? serializeIdScalar(value) : JSON.stringify(value);
}

function serializeNode(node: WidgetFilterRule | WidgetFilterGroup): string {
  if (isGroup(node)) {
    return `{"operator":${JSON.stringify(node.operator)},"rules":[${node.rules
      .map(serializeNode)
      .join(',')}]}`;
  }
  const parts = [
    `"field":${JSON.stringify(node.field)}`,
    `"operator":${JSON.stringify(node.operator)}`,
  ];
  if (node.value !== undefined) {
    parts.push(`"value":${serializeValue(node.field, node.value)}`);
  }
  return `{${parts.join(',')}}`;
}

/** Serializes a filter group into the `query.filtersJson` string, preserving full precision on Snowflake IDs. */
export function serializeFilterGroup(group: WidgetFilterGroup): string {
  return serializeNode(group);
}

/**
 * Drops empty subgroups (a group the user added but never filled), which the
 * backend would otherwise evaluate as an always-true branch. Returns undefined
 * when nothing filterable is left, so the caller can omit `filtersJson`.
 */
export function pruneFilterGroup(group: WidgetFilterGroup): WidgetFilterGroup | undefined {
  const rules: Array<WidgetFilterRule | WidgetFilterGroup> = [];
  for (const node of group.rules) {
    if (!isGroup(node)) {
      rules.push(node);
      continue;
    }
    const pruned = pruneFilterGroup(node);
    if (pruned) rules.push(pruned);
  }
  return rules.length > 0 ? { ...group, rules } : undefined;
}

// Quotes bare integers of 16+ digits (beyond Number.MAX_SAFE_INTEGER's ~16
// digit range) before JSON.parse touches them, so id values round-trip as
// strings instead of being rounded to the nearest representable double.
function quoteBigInts(json: string): string {
  return json.replace(/([:,[])(-?\d{16,})(?=[,\]}])/g, '$1"$2"');
}

/** Parses a `query.filtersJson` string back into a filter group for form defaults, without losing ID precision. */
export function parseFilterGroup(filtersJson?: string): WidgetFilterGroup | undefined {
  if (!filtersJson) return undefined;
  try {
    return JSON.parse(quoteBigInts(filtersJson)) as WidgetFilterGroup;
  } catch {
    return undefined;
  }
}
